import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Phone, 
  MapPin,
  Users,
  Mail
} from 'lucide-react';
import { getAllUsers, createUser, updateUser, deleteUser, getAllDebts } from '../services/api';
import { User as UserType, Debt } from '../types';

const MembersContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 20px 12px 45px;
  border: 2px solid #e1e5e9;
  border-radius: 25px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  size: 20px;
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const MemberCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
  }
`;

const MemberHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
`;

const MemberPhone = styled.div`
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
`;

const MemberAddress = styled.div`
  color: #666;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const MemberActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ variant: 'edit' | 'delete' }>`
  width: 35px;
  height: 35px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'edit' ? `
    background: #e3f2fd;
    color: #1976d2;
    
    &:hover {
      background: #bbdefb;
    }
  ` : `
    background: #ffebee;
    color: #d32f2f;
    
    &:hover {
      background: #ffcdd2;
    }
  `}
`;

const NotificationPrefs = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
`;

const PrefItem = styled.div<{ enabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: ${props => props.enabled ? '#28a745' : '#666'};
  font-weight: 500;
`;

const DebtStatus = styled.div<{ status: 'no-debt' | 'debt' | 'credit' }>`
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  border: 1px solid;
  
  ${props => {
    switch (props.status) {
      case 'no-debt':
        return `
          background: #f8f9fa;
          color: #6c757d;
          border-color: #e9ecef;
        `;
      case 'debt':
        return `
          background: #fff3cd;
          color: #856404;
          border-color: #ffeaa7;
        `;
      case 'credit':
        return `
          background: #d4edda;
          color: #155724;
          border-color: #c3e6cb;
        `;
      default:
        return '';
    }
  }}
`;

const Modal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 10px;
`;

const CheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #667eea;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const Members: React.FC = () => {
  const [members, setMembers] = useState<UserType[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<UserType[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    phone: '',
    address: '',
    additionalPhone: '',
    email: '',
    isAdmin: false,
    notificationPreferences: {
      whatsapp: true,
      sms: true,
      calls: false,
      email: false
    }
  });

  useEffect(() => {
    loadMembers();
  }, []);

  useEffect(() => {
    const filtered = members.filter(member =>
      `${member.name} ${member.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.phone.includes(searchTerm)
    );
    setFilteredMembers(filtered);
  }, [members, searchTerm]);

  const loadMembers = async () => {
    try {
      const [membersData, debtsData] = await Promise.all([
        getAllUsers(),
        getAllDebts()
      ]);
      setMembers(membersData);
      setDebts(debtsData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  // Calculate member's debt status
  const getMemberDebtStatus = (memberId: string): { status: 'no-debt' | 'debt' | 'credit'; amount: number; text: string } => {
    const memberDebts = debts.filter(debt => debt.memberId === memberId);
    
    if (memberDebts.length === 0) {
      return { status: 'no-debt', amount: 0, text: 'אין חובות' };
    }
    
    const totalDebt = memberDebts.reduce((sum, debt) => sum + debt.amount, 0);
    const totalPaid = memberDebts.reduce((sum, debt) => sum + debt.paidAmount, 0);
    const remaining = totalDebt - totalPaid;
    
    if (remaining <= 0) {
      const credit = Math.abs(remaining);
      return { 
        status: 'credit', 
        amount: credit, 
        text: `יתרה: ₪${credit.toLocaleString()}` 
      };
    } else {
      return { 
        status: 'debt', 
        amount: remaining, 
        text: `חוב: ₪${remaining.toLocaleString()}` 
      };
    }
  };

  const handleAddMember = () => {
    setEditingMember(null);
    setFormData({
      name: '',
      lastName: '',
      phone: '',
      address: '',
      additionalPhone: '',
      email: '',
      isAdmin: false,
      notificationPreferences: {
        whatsapp: true,
        sms: true,
        calls: false,
        email: false
      }
    });
    setIsModalOpen(true);
  };

  const handleEditMember = (member: UserType) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      lastName: member.lastName,
      phone: member.phone,
      address: member.address || '',
      additionalPhone: member.additionalPhone || '',
      email: member.email || '',
      isAdmin: member.isAdmin,
      notificationPreferences: {
        whatsapp: member.notificationPreferences?.whatsapp ?? true,
        sms: member.notificationPreferences?.sms ?? true,
        calls: member.notificationPreferences?.calls ?? false,
        email: member.notificationPreferences?.email ?? false
      }
    });
    setIsModalOpen(true);
  };

  const handleSaveMember = async () => {
    try {
      // Check if phone number already exists (only for new members)
      if (!editingMember) {
        const existingUser = members.find(member => member.phone === formData.phone);
        if (existingUser) {
          alert('מספר הטלפון כבר קיים במערכת');
          return;
        }
      }
      
      if (editingMember) {
        await updateUser(editingMember.id, formData);
      } else {
        await createUser(formData);
      }
      await loadMembers();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving member:', error);
    }
  };

  const handleDeleteMember = async (memberId: string) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק את המתפלל?')) {
      try {
        await deleteUser(memberId);
        await loadMembers();
      } catch (error) {
        console.error('Error deleting member:', error);
      }
    }
  };

  if (loading) {
    return (
      <MembersContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          טוען נתונים...
        </div>
      </MembersContainer>
    );
  }

  return (
    <MembersContainer>
      <Header>
        <Title>
          <Users size={28} />
          ניהול מתפללים
        </Title>
        <SearchBar>
          <SearchInput
            type="text"
            placeholder="חיפוש מתפלל..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon size={20} />
        </SearchBar>
        <AddButton onClick={handleAddMember}>
          <Plus size={20} />
          הוסף מתפלל
        </AddButton>
      </Header>

      {filteredMembers.length === 0 ? (
        <EmptyState>
          <Users size={48} style={{ marginBottom: '20px', opacity: 0.5 }} />
          <h3>אין מתפללים במערכת</h3>
          <p>לחץ על "הוסף מתפלל" כדי להתחיל</p>
        </EmptyState>
      ) : (
        <MembersGrid>
          {filteredMembers.map((member) => (
            <MemberCard key={member.id}>
              <MemberHeader>
                <MemberInfo>
                  <MemberName>{member.name} {member.lastName}</MemberName>
                  <MemberPhone>
                    <Phone size={16} />
                    {member.phone}
                  </MemberPhone>
                  {member.address && (
                    <MemberAddress>
                      <MapPin size={16} />
                      {member.address}
                    </MemberAddress>
                  )}
                  {member.email && (
                    <MemberAddress>
                      <Mail size={16} />
                      {member.email}
                    </MemberAddress>
                  )}
                </MemberInfo>
                <MemberActions>
                  <ActionButton
                    variant="edit"
                    onClick={() => handleEditMember(member)}
                    title="ערוך"
                  >
                    <Edit size={16} />
                  </ActionButton>
                  <ActionButton
                    variant="delete"
                    onClick={() => handleDeleteMember(member.id)}
                    title="מחק"
                  >
                    <Trash2 size={16} />
                  </ActionButton>
                </MemberActions>
              </MemberHeader>
              
              <NotificationPrefs>
                <PrefItem enabled={member.notificationPreferences.whatsapp}>
                  WhatsApp
                </PrefItem>
                <PrefItem enabled={member.notificationPreferences.sms}>
                  SMS
                </PrefItem>
                <PrefItem enabled={member.notificationPreferences.calls}>
                  שיחות
                </PrefItem>
              </NotificationPrefs>
              
              <DebtStatus status={getMemberDebtStatus(member.id).status}>
                {getMemberDebtStatus(member.id).text}
              </DebtStatus>
            </MemberCard>
          ))}
        </MembersGrid>
      )}

      <Modal isOpen={isModalOpen} onClick={() => setIsModalOpen(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {editingMember ? 'ערוך מתפלל' : 'הוסף מתפלל חדש'}
            </ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>
              ×
            </CloseButton>
          </ModalHeader>

          <FormGroup>
            <Label>שם פרטי *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>שם משפחה *</Label>
            <Input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>מספר טלפון *</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>כתובת</Label>
            <Input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>טלפון נוסף</Label>
            <Input
              type="tel"
              value={formData.additionalPhone}
              onChange={(e) => setFormData({ ...formData, additionalPhone: e.target.value })}
            />
          </FormGroup>

          <FormGroup>
            <Label>כתובת אימייל</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="example@domain.com"
            />
          </FormGroup>

          <FormGroup>
            <CheckboxItem>
              <Checkbox
                type="checkbox"
                checked={formData.isAdmin}
                onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })}
              />
              מנהל מערכת
            </CheckboxItem>
          </FormGroup>

          <FormGroup>
            <Label>העדפות התראות</Label>
            <CheckboxGroup>
              <CheckboxItem>
                <Checkbox
                  type="checkbox"
                  checked={formData.notificationPreferences.whatsapp}
                  onChange={(e) => setFormData({
                    ...formData,
                    notificationPreferences: {
                      ...formData.notificationPreferences,
                      whatsapp: e.target.checked
                    }
                  })}
                />
                WhatsApp
              </CheckboxItem>
              <CheckboxItem>
                <Checkbox
                  type="checkbox"
                  checked={formData.notificationPreferences.sms}
                  onChange={(e) => setFormData({
                    ...formData,
                    notificationPreferences: {
                      ...formData.notificationPreferences,
                      sms: e.target.checked
                    }
                  })}
                />
                SMS
              </CheckboxItem>
              <CheckboxItem>
                <Checkbox
                  type="checkbox"
                  checked={formData.notificationPreferences.calls}
                  onChange={(e) => setFormData({
                    ...formData,
                    notificationPreferences: {
                      ...formData.notificationPreferences,
                      calls: e.target.checked
                    }
                  })}
                />
                שיחות
              </CheckboxItem>
              <CheckboxItem>
                <Checkbox
                  type="checkbox"
                  checked={formData.notificationPreferences.email}
                  onChange={(e) => setFormData({
                    ...formData,
                    notificationPreferences: {
                      ...formData.notificationPreferences,
                      email: e.target.checked
                    }
                  })}
                />
                אימייל
              </CheckboxItem>
            </CheckboxGroup>
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              ביטול
            </Button>
            <Button variant="primary" onClick={handleSaveMember}>
              {editingMember ? 'עדכן' : 'הוסף'}
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </MembersContainer>
  );
};

export default Members;
